import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Truck, Boxes, Package, Users, Coins, Mail, BarChart3, FileText, Settings, Rocket, Inbox, LogOut, Menu } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Inp, L } from "@/admin/ui";

const Ctx = createContext(null);
export const useStaff = () => useContext(Ctx);

const NAV = [
  ["/admin", "Overview", LayoutDashboard, "orders.view_limited"], ["/admin/orders", "Orders", ShoppingCart, "orders.view_limited"], ["/admin/fulfilment", "Fulfilment", Truck, "fulfilment"],
  ["/admin/inventory", "Inventory and batches", Boxes, "inventory.view"], ["/admin/products", "Products", Package, "products"], ["/admin/customers", "Customers", Users, "customers.view"],
  ["/admin/credits", "Credits", Coins, "credits.view"], ["/admin/marketing", "Marketing", Mail, "marketing"], ["/admin/reports", "Reports", BarChart3, "reports"],
  ["/admin/content", "Content", FileText, "content"], ["/admin/enquiries", "Enquiries", Inbox, "enquiries"], ["/admin/settings", "Settings", Settings, "orders.view_limited"], ["/admin/launch", "Launch checklist", Rocket, "orders.view_limited"],
];

export const can = (staff, p) => staff && (staff.permissions.includes("*") || staff.permissions.includes(p));

export function AdminShell() {
  const [staff, setStaff] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get("/admin/auth/me").then((r) => setStaff(r.data)).catch(() => setStaff(false)); }, []);
  if (staff === null) return <div className="admin p-10 text-sm">Loading…</div>;
  if (staff === false) return <Navigate to="/admin/login" replace />;
  const logout = async () => { await api.post("/admin/auth/logout"); nav("/admin/login"); };
  return (
    <Ctx.Provider value={staff}>
      <div className="admin min-h-screen bg-slate-50 text-slate-900 md:flex" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
        <aside className={`${open ? "block" : "hidden"} md:block md:w-60 bg-[#173B2D] text-[#F8F5E9] md:min-h-screen md:sticky md:top-0 md:h-screen overflow-y-auto`}>
          <div className="px-5 py-5 border-b border-white/10"><Link to="/admin" className="font-semibold tracking-widest text-sm">HONEY NUTS · ADMIN</Link></div>
          <nav className="py-3" aria-label="Admin">
            {NAV.filter((n) => can(staff, n[3])).map(([to, label, Icon]) => (
              <NavLink key={to} to={to} end={to === "/admin"} onClick={() => setOpen(false)} data-testid={`admin-nav-${label.split(" ")[0].toLowerCase()}`}
                className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm ${isActive ? "bg-white/10 text-white" : "text-white/75 hover:text-white"}`}>
                <Icon size={16} />{label}
              </NavLink>
            ))}
          </nav>
          <div className="px-5 py-4 border-t border-white/10 text-xs">
            <p className="truncate">{staff.email}</p><p className="capitalize text-white/60">{staff.role}</p>
            <button onClick={logout} className="mt-3 flex items-center gap-2 text-white/80 hover:text-white" data-testid="admin-logout-btn"><LogOut size={14} />Sign out</button>
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b"><span className="font-semibold text-sm">HONEY NUTS Admin</span><button onClick={() => setOpen(!open)} aria-label="Menu" data-testid="admin-mobile-menu"><Menu size={20} /></button></div>
          <main className="p-4 md:p-8 max-w-7xl"><Outlet /></main>
        </div>
      </div>
    </Ctx.Provider>
  );
}

const Box = ({ title, children }) => (
  <div className="admin min-h-screen bg-slate-50 flex items-center justify-center p-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
    <div className="bg-white border border-slate-200 rounded-lg p-8 w-full max-w-sm"><p className="text-xs tracking-widest text-[#A66B24] font-semibold mb-1">HONEY NUTS</p><h1 className="text-xl font-semibold mb-6">{title}</h1>{children}</div>
  </div>
);

export function AdminLogin() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", totp: "" });
  const [mfa, setMfa] = useState(false);
  const [st, setSt] = useState({});
  const [setup, setSetup] = useState(null);
  useEffect(() => { api.get("/admin/setup/status").then((r) => setSetup(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setSt({ loading: true });
    try { await api.post("/admin/auth/login", { ...f, totp: f.totp || null }); nav("/admin"); } catch (err) {
      const m = errMsg(err);
      if (m === "mfa_required") { setMfa(true); setSt({}); } else setSt({ error: m });
    }
  };
  return (
    <Box title="Staff sign in">
      <form onSubmit={submit} className="grid gap-4" data-testid="admin-login-form">
        <L label="Email"><Inp type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email-input" required /></L>
        <L label="Password"><Inp type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password-input" required /></L>
        {mfa && <><p className="text-sm text-slate-600" data-testid="mfa-required-msg">Enter the 6-digit code from your authenticator app.</p><L label="Authenticator code"><Inp inputMode="numeric" autoComplete="one-time-code" value={f.totp} onChange={(e) => setF({ ...f, totp: e.target.value })} data-testid="admin-totp-input" /></L></>}
        {st.error && <p className="text-sm text-red-700" role="alert" data-testid="admin-login-error">{st.error}</p>}
        <Btn disabled={st.loading} data-testid="admin-login-submit">{st.loading ? "Signing in…" : "Sign in"}</Btn>
      </form>
      {setup && !setup.owner_exists && <p className="text-xs text-slate-600 mt-6">No owner account yet. <Link to="/admin/setup" className="underline" data-testid="owner-setup-link">Set up the owner account</Link></p>}
      <Link to="/forgot-password" className="block text-xs underline mt-3">Forgot password?</Link>
    </Box>
  );
}

export function AdminSetup() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [pw, setPw] = useState("");
  const [st, setSt] = useState({});
  const run = async (fn) => { setSt({ loading: true }); try { const r = await fn(); setSt({ done: r.data.message || "Done" }); } catch (e) { setSt({ error: errMsg(e) }); } };
  return (
    <Box title="Owner account setup">
      {st.done ? <><p className="text-sm" data-testid="owner-setup-done">{token ? "Owner account ready. You can now sign in." : st.done}</p><Link to="/admin/login" className="underline text-sm block mt-4">Go to sign in</Link></> : token ? (
        <form onSubmit={(e) => { e.preventDefault(); run(() => api.post("/admin/setup/complete", { token, password: pw })); }} className="grid gap-4">
          <L label="Choose an owner password (10+ characters, letters and numbers)"><Inp type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="owner-password-input" /></L>
          <Btn disabled={st.loading}>Create owner account</Btn>
        </form>
      ) : (
        <>
          <p className="text-sm text-slate-600 mb-4">A one-time setup link will be emailed to the owner address configured on the server. There is no default password.</p>
          <Btn onClick={() => run(() => api.post("/admin/setup/request"))} disabled={st.loading} data-testid="owner-setup-request-btn">Email me a setup link</Btn>
        </>
      )}
      {st.error && <p className="text-sm text-red-700 mt-3" role="alert">{st.error}</p>}
    </Box>
  );
}
